import { CloseCircleFilled } from '@ant-design/icons';
import { Button, Input, Segmented, Select, SelectProps, Space } from 'antd';
import { ReactNode, useEffect, useState } from 'react';

import useFetchRecordContext from '../hooks/use-fetch-record/use-fetch-record-context';

type FilterControl = (props: {
    onApply: (value: any) => void;
    setFilter: (filter: Record<string, any>, value: any) => void;
    onCancel: () => void;
    params?: Record<string, any>;
}) => ReactNode;

// Collapses the filter back to its button when the dropdown is dismissed
// without a selection, so it doesn't get stuck open.
export function FilterSelect({
    onApply,
    onCancel,
    onChange,
    ...selectProps
}: SelectProps & {
    onApply: (value: any) => void;
    onCancel: () => void;
}) {
    return (
        <Select
            size="small"
            defaultOpen
            {...selectProps}
            onChange={onChange ?? onApply}
            onDropdownVisibleChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
        />
    );
}

export interface FilterOptions {
    search?: string;
    searchPlaceholder?: string;
    filters?: {
        [name: string]: {
            label: string;
            control: FilterControl;
            noToggleButton?: boolean;
            paramKeys?: string[];
            onReset?: (
                setFilter: (filter: Record<string, any>) => void
            ) => void;
            displayValue?: (value: any) => ReactNode;
        };
    };
    tabs?: {
        field: string;
        options: {
            label: string;
            value: string;
        }[];
    };
}

enum FilterStates {
    Closed,
    Open,
    Applied,
}

function Filter(props: {
    name: string;
    label: string;
    control: FilterControl;
    params?: Record<string, any>;
    noToggleButton?: boolean;
    onApply: (value: any) => void;
    onReset?: (setFilter: (filter: Record<string, any>) => void) => void;
    setFilter: (filter: Record<string, any>) => void;
    displayValue?: (value: any) => ReactNode;
    paramKeys?: string[];
}) {
    const { params } = useFetchRecordContext();

    const [filterState, setFilterState] = useState<FilterStates>(
        FilterStates.Closed
    );
    const [filterValue, setFilterValue] = useState<ReactNode>();

    const ControlEl = props.control;

    useEffect(() => {
        let value = params?.[props.name];

        if (!value && props.paramKeys?.length && params) {
            value = Object.entries(params)
                .filter(([key, _]) => props.paramKeys!.includes(key))
                .map(([_, val]) => val)
                .filter((v) => Boolean(v));
        }

        if (value === undefined || (value !== null && !value.length)) {
            return;
        }

        if (props.displayValue) {
            setFilterValue(props.displayValue(value));
        } else {
            setFilterValue(value);
        }
        setFilterState(FilterStates.Applied);
    }, [params, props.name]);

    return (
        <>
            {filterState === FilterStates.Closed && !props.noToggleButton && (
                <Button
                    size="small"
                    onClick={() => setFilterState(FilterStates.Open)}
                >
                    {props.label}
                </Button>
            )}
            {(filterState === FilterStates.Open || props.noToggleButton) && (
                <ControlEl
                    params={params}
                    setFilter={(filter: Record<string, any>, value) => {
                        props.setFilter(filter);

                        if (props.displayValue) {
                            setFilterValue(props.displayValue(value));
                        } else {
                            setFilterValue(value);
                        }

                        setFilterState(FilterStates.Applied);
                    }}
                    onApply={(value) => {
                        props.onApply(value);

                        if (props.displayValue) {
                            setFilterValue(props.displayValue(value));
                        } else {
                            setFilterValue(value);
                        }

                        setFilterState(FilterStates.Applied);
                    }}
                    onCancel={() =>
                        setFilterState((state) =>
                            state === FilterStates.Open
                                ? FilterStates.Closed
                                : state
                        )
                    }
                />
            )}
            {filterState === FilterStates.Applied && !props.noToggleButton && (
                <Button
                    icon={<CloseCircleFilled />}
                    size="small"
                    type="primary"
                    onClick={() => {
                        if (props.onReset) {
                            props.onReset(props.setFilter);
                        } else {
                            props.onApply(undefined);
                        }
                        setFilterValue(undefined);
                        setFilterState(FilterStates.Closed);
                    }}
                >
                    {props.label}: <b>{filterValue}</b>
                </Button>
            )}
        </>
    );
}

export function Filters(props: FilterOptions) {
    const { loading, fetchRecord, setParams, params } = useFetchRecordContext();

    const [search, setSearch] = useState('');

    const setFilter = (filter: Record<string, any>) => {
        const newParams = {
            ...params,
            ...filter,
            page: 1,
        };

        if (setParams) {
            setParams(newParams);
        }

        // biome-ignore lint/suspicious/noConsole: used for debugging
        fetchRecord(newParams).catch((e) => console.error(e));
    };

    useEffect(() => {
        if (props.search && params?.[props.search]) {
            setSearch(params[props.search]);
        }
    }, [params, props.search]);

    return (
        <>
            {props.search && (
                <Space.Compact>
                    <Input.Search
                        style={{ width: 300 }}
                        disabled={!!loading}
                        value={search}
                        placeholder={props.searchPlaceholder}
                        allowClear
                        onChange={(q) => setSearch(q.currentTarget.value)}
                        onSearch={(value) => {
                            setFilter({ [props.search!]: value });
                        }}
                    />
                </Space.Compact>
            )}

            {props.filters && (
                <Space
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                    }}
                >
                    Filters:
                    {Object.entries(props.filters).map((filter) => (
                        <Filter
                            key={`filter-${filter[0]}`}
                            params={params}
                            name={filter[0]}
                            {...filter[1]}
                            onApply={(value) =>
                                setFilter({ [filter[0]]: value })
                            }
                            setFilter={setFilter}
                        />
                    ))}
                </Space>
            )}

            {props.tabs && (
                <Segmented
                    options={props.tabs.options}
                    onChange={(value) =>
                        setFilter({ [props.tabs!.field]: value })
                    }
                />
            )}
        </>
    );
}
